<template>
  <section class="hero">
    <div v-if="!loadingInitial && !loadingError && tripData" class="hero-content">
      <h1>De {{ tripData.addressToStart.city.name }} à {{ tripData.addressToEnd.city.name }}</h1>
      <p>Pour seulement {{ formatPrice(tripData.price) }}</p>
      <div class="search-box-container can-hidden">
        <SearchBox :startCity="tripData.addressToStart.city.slug" :endCity="tripData.addressToEnd.city.slug"
          :date="searchDate" />
      </div>
    </div>
  </section>


  <div class="page-container">
    <LoaderOverlay v-if="loadingInitial || submitting" />

    <EmptyState v-if="loadingError && !loadingInitial" :error="loadingError" />

    <div v-else-if="tripData" class="trip-page">
      <!-- Cartes Départ / Arrivée -->
      <div class="route-cards">
        <!-- <div class="trip-route-header"> -->
        <!-- <h2>Départ</h2> -->
        <!-- <h2>Arrivée</h2> -->
        <!-- </div> -->
        <!-- Départ -->
        <div class="card route-card route-card--start">
          <div v-if="tripData.addressToStart.thumbnailName" class="card-thumbnail">
            <span class="label-start">Départ</span>
            <img :src="`${config.uploadsUrl}/thumbnails/${tripData.addressToStart.thumbnailName}`"
              :alt="tripData.addressToStart.city.name" />
          </div>
          <div v-else class="card-thumbnail placeholder">
            📍
          </div>
          <div class="card-content">
            <div class="card-body">
              <strong class="city-name">{{ tripData.addressToStart.city.name }}</strong>
              <span class="address">{{ tripData.addressToStart.street }}</span>
              <span class="time">{{ formatDateTime(tripData.timeToStart) }}</span>
            </div>
          </div>
        </div>

        <!-- Ligne pointillée avec durée -->
        <div class="trip-connector-container">
          <div class="trip-connector">
            <div v-if="tripDuration" class="duration-badge">{{ tripDuration }}</div>
          </div>
        </div>

        <!-- Arrivée -->
        <div class="card route-card route-card--end">
          <div v-if="tripData.addressToEnd.thumbnailName" class="card-thumbnail">
            <span class="label-end">Arrivée</span>
            <img :src="`${config.uploadsUrl}/thumbnails/${tripData.addressToEnd.thumbnailName}`"
              :alt="tripData.addressToEnd.city.name" />
          </div>
          <div v-else class="card-thumbnail placeholder">
            🏁
          </div>
          <div class="card-content">
            <div class="card-body">
              <strong class="city-name">{{ tripData.addressToEnd.city.name }}</strong>
              <span class="address">{{ tripData.addressToEnd.street }}</span>
              <span class="time">{{ formatDateTime(tripData.timeToEnd) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Autres informations du voyage -->
      <div class="trip-details">
        <h2>Informations du voyage</h2>
        <div class="card card-details no-shadow">
          <div class="card-content">

            <div v-if="tripData.driver" class="card-item card-item-driver">
              <strong>Chauffeur</strong>
              <div class="driver-info-simple">
                <span>{{ tripData.driver.firstName }} {{ tripData.driver.lastName }}</span>
                <div class="driver-avatar-simple">
                  <img v-if="tripData.driver.avatarName"
                    :src="`${config.uploadsUrl}/avatars/${tripData.driver.avatarName}`"
                    :alt="`${tripData.driver.firstName} ${tripData.driver.lastName}`" />
                  <div v-else class="driver-avatar-placeholder">
                    {{ tripData.driver.firstName.charAt(0) }}{{ tripData.driver.lastName.charAt(0) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="card-item">
              <strong>Compagnie</strong>
              <div class="company-info">
                <span>
                  <RouterLink :to="{ name: 'company', params: { slug: tripData.company.slug } }">{{
                    tripData.company.name }}</RouterLink>
                </span>
                <StarRating :rating="tripData.company.averageRating" :count="tripData.company.ratingsCount" />
              </div>
            </div>

            <div class="card-item">
              <strong>Places disponibles</strong>
              <span>{{ tripData.availablePlaces }}/{{ tripData.vehicle.nbPlaces }}</span>
            </div>

            <div v-if="tripData.vehicle.airConditioning" class="card-item">
              <strong>Climatisation</strong>
              <span>✅ Oui</span>
            </div>

            <div v-if="tripData.vehicle.toilet" class="card-item">
              <strong>Toilettes</strong>
              <span>✅ Oui</span>
            </div>

            <div v-if="tripData.vehicle.tv" class="card-item">
              <strong>TV</strong>
              <span>✅ Oui</span>
            </div>

            <div v-if="tripData.vehicle.refreshments" class="card-item">
              <strong>Restauration</strong>
              <span>✅ Oui</span>
            </div>

            <!--
<div class="card-item>
<strong>Immatriculation</strong>
<span class="immat">{{ tripData.vehicle.noImmat }}</span>
</div>
-->

            <div class="card-item card-item--last">
              <strong>Prix par personne</strong>
              <span class="price">{{ formatPrice(tripData.price) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Section passagers -->
      <div v-if="!tripHasStarted && availablePlaces > 0" class="passengers-section">
        <div class="passengers-header">
          <h2>Passagers</h2>
          <button @click="addNewPassenger" class="btn btn-secondary" type="button" :disabled="passengers.length >= 5">
            + Ajouter un passager
          </button>
        </div>
        <div class="passengers-list">
          <div v-for="(passenger, index) in passengers" :key="index" class="passenger-item">
            <div class="passenger-inputs">
              <select v-model="passenger.class" class="input-field">
                <option value="Classic">Classic</option>
                <option value="Premium">Premium</option>
              </select>

              <select v-model="passenger.type" class="input-field">
                <option value="Adult">Adulte</option>
                <option value="Child">Enfant</option>
              </select>

              <div class="bags-control">
                <label>Bagages:</label>
                <input type="number" v-model.number="passenger.bags" min="0" max="5" class="input-field small" />
              </div>
            </div>

            <button v-if="passengers.length > 1" @click="removePassenger(index)" class="btn-remove" type="button">
              ✕
            </button>
          </div>
        </div>
      </div>

      <!-- Paiement - moyen et téléphone sur la même ligne desktop -->
      <div v-if="!tripHasStarted && availablePlaces > 0" class="payment-section" :class="{
        'disabled':
          tripData.availablePlaces === 0
      }">
        <h2>Paiement</h2>
        <div class="card">
          <div class="card-content">
            <div class="payment-row">
              <div class="card-item card-item--paiment">
                <label for="paiment">Moyen de paiement</label>
                <select v-model="paymentMethod" class="input-field" id="paiment"
                  :disabled="!canSkipPayment && !hasAvailablePaymentMethods || tripData.availablePlaces === 0">
                  <option v-for="paymentOption in paymentOptions" :key="paymentOption.value"
                    :value="paymentOption.value">
                    {{ paymentOption.label }}
                  </option>
                  <option value="" v-if="canSkipPayment">Paiement en espèces</option>
                </select>
                <small v-if="!canSkipPayment && !hasAvailablePaymentMethods" class="payment-help">
                  Aucun moyen de paiement n'est encore disponible pour {{ currentCountry?.name || 'ce pays' }}.
                </small>
              </div>

              <div v-if="showPhoneField" class="card-item card-item--phone">
                <div v-if="canSkipPayment">
                  <label>Pays</label>
                  <select class="input-field" v-model="cashCountryIso">
                    <option value="" disabled>Sélectionner</option>
                    <option v-for="c in countries" :key="c.iso3" :value="c.iso3">{{ c.name }}</option>
                  </select>
                </div>
                <div>
                  <label>{{ phoneLabel }}</label>
                  <input type="tel" v-model="phone" :placeholder="phoneLabel" class="input-field"
                    :disabled="isPhoneDisabled || tripData.availablePlaces === 0" />
                </div>
              </div>
            </div>

            <div class="card-item card-item--last price-item">
              <strong>Total</strong>
              <span class="price-total">{{ formatPrice(totalPrice) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Proposition d'inscription pour utilisateurs anonymes -->
      <div v-if="isAnonymous && !tripHasStarted" class="registration-prompt-section">
        <InlineRegistrationForm ref="registrationFormRef" :phone-value="phone" :phone-disabled="false"
          :registration-success="registrationSuccess" @registration-success="handleRegistrationSuccess"
          @registration-error="handleRegistrationError" />
      </div>

      <!-- Boutons d'action -->
      <div class="action-buttons">
        <button class="btn btn-secondary" @click="$router.back()" :disabled="submitting">
          Retour
        </button>
        <button v-if="!tripHasStarted && availablePlaces > 0" class="btn btn-primary"
          :class="{ disabled: tripData.availablePlaces === 0 }" @click="createReservation"
          :disabled="submitting || (!canSkipPayment && !hasAvailablePaymentMethods)">
          Confirmer la réservation
        </button>
      </div>

      <!-- Formulaire de notation (si voyage terminé) -->
      <RatingForm v-if="canRate" :trip-uuid="route.params.tripId" :company-id="tripData.company.id"
        :driver-id="driverId" @rating-submitted="handleRatingSubmitted" />
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'TripPage' })

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'

import LoaderOverlay from '../components/LoaderOverlay.vue'
import EmptyState from '../components/EmptyState.vue'
import InlineRegistrationForm from '../components/Auth/InlineRegistrationForm.vue'
import RatingForm from '@/components/RatingForm.vue'
import StarRating from '@/components/StarRating.vue'
import SearchBox from '@/components/SearchBox.vue'

import { sleep } from '@/utils/sleep'
import { config } from '@/config/env.js'
import { useUserStore } from '@/stores/userStore'
import { useLocaleStore } from '@/stores/localeStore'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const userStore = useUserStore()
const localeStore = useLocaleStore()

const { countries, currentCountry, availablePaymentMethods } = storeToRefs(localeStore)

const tripData = ref(null)
const loadingInitial = ref(true)
const submitting = ref(false)
const loadingError = ref(null)
const phone = ref('')
const passengers = ref([{ class: 'Classic', type: 'Adult', bags: 0 }])
const registrationFormRef = ref(null)
const pendingRegistrationData = ref(null)
const registrationSuccess = ref(false)
const userHasRated = ref(false)
const userCanRate = ref(false)

const minTime = sleep(800)
// const cashCountryIso = ref(localeStore.countryIso)
const cashCountryIso = ref('')

const driverId = computed(() => {
  const id = tripData.value?.driver?.id
  return id ? parseInt(id, 10) : null
})

const tripHasStarted = computed(() => {
  if (!tripData.value?.timeToStart) return false
  return new Date() >= new Date(tripData.value.timeToStart)
})

const availablePlaces = computed(() => {
  return tripData.value ? tripData.value.availablePlaces : 0
})

const tripHasEnded = computed(() => {
  if (!tripData.value?.timeToEnd) return false
  return new Date() >= new Date(tripData.value.timeToEnd)
})

const canRate = computed(() => {
  return !!userStore.user && tripHasEnded.value && !userHasRated.value && userCanRate.value
})

const searchDate = computed(() => {
  if (!tripData.value?.timeToStart) return ''
  return tripData.value.timeToStart.split('T')[0].split(' ')[0]
})

const totalPrice = computed(() => {
  if (!tripData.value) return 0

  const base = tripData.value.price

  return passengers.value.reduce((sum, p) => {
    let price = base
    if (p.class === 'Premium') price *= 1.5
    price += p.bags * 10
    return sum + price
  }, 0)
})

const canSkipPayment = computed(() => {
  if (!userStore.user || !Array.isArray(userStore.user.roles)) return false

  return userStore.user.roles.includes('ROLE_ADMIN')
    || userStore.user.roles.includes('ROLE_SELLER')
})

const paymentOptions = computed(() =>
  canSkipPayment.value
    ? localeStore.getPaymentMethods({ includeAll: true })
    : availablePaymentMethods.value
)

const hasAvailablePaymentMethods = computed(() => paymentOptions.value.length > 0)

const isAuthenticatedRegularUser = computed(() => {
  return !!userStore.user && !canSkipPayment.value
})

const isAnonymous = computed(() => !userStore.user)

const resolveDefaultPaymentMethod = () => {
  if (canSkipPayment.value) return ''

  const favoritePaymentMethod = userStore.user?.favoritePaymentMethod
  if (
    favoritePaymentMethod &&
    paymentOptions.value.some(method => method.value === favoritePaymentMethod)
  ) {
    return favoritePaymentMethod
  }

  return paymentOptions.value[0]?.value ?? ''
}

const paymentMethod = ref(resolveDefaultPaymentMethod())

watch(
  [paymentOptions, canSkipPayment, () => userStore.user?.favoritePaymentMethod],
  () => {
    if (canSkipPayment.value && paymentMethod.value === '') return

    const stillAvailable = paymentOptions.value.some(
      method => method.value === paymentMethod.value
    )

    if (stillAvailable) return

    paymentMethod.value = resolveDefaultPaymentMethod()
  },
  { immediate: true }
)

watch(paymentMethod, (newVal) => {
  if (newVal === '') {
    phone.value = ''
    return
  }

  if (isAuthenticatedRegularUser.value && userStore.user?.phone) {
    phone.value = userStore.user.phone
  }
})

watch(
  () => userStore.user,
  async (newUser) => {
    if (newUser && tripData.value) {
      await checkIfUserHasRated()
    }
  }
)

watch(tripData, async (newTripData) => {
  if (newTripData && userStore.user) {
    await checkIfUserHasRated()
  }
})

const isPhoneDisabled = computed(() => {
  return !!(
    isAuthenticatedRegularUser.value &&
    paymentMethod.value !== '' &&
    userStore.user?.phone
  )
})

const showPhoneField = computed(() => {
  if (registrationSuccess.value) return false
  return paymentMethod.value !== '' || canSkipPayment.value
})

const phoneLabel = computed(() => {
  if (canSkipPayment.value && paymentMethod.value === '') {
    return 'Numéro du client'
  }
  return 'Numéro de téléphone'
})

const tripDuration = computed(() => {
  if (!tripData.value?.timeToStart || !tripData.value?.timeToEnd) return null

  const start = new Date(tripData.value.timeToStart)
  const end = new Date(tripData.value.timeToEnd)
  const durationMs = end - start

  if (durationMs <= 0) return null

  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`
  if (hours > 0) return `${hours}h`
  return `${minutes}min`
})

const checkIfUserHasRated = async () => {
  if (!userStore.user || !tripHasEnded.value) return

  try {
    const response = await fetch(
      `${config.apiUrl}/ratings/user-has-rated/${route.params.tripId}`,
      { credentials: 'include' }
    )

    if (!response.ok) return

    const data = await response.json()
    userHasRated.value = data.hasRated || false
    userCanRate.value = data.canRate || false
  } catch (err) {
    console.error('Erreur lors de la vérification du rating:', err)
  }
}

const handleRatingSubmitted = () => {
  userHasRated.value = true
}

onMounted(async () => {
  try {
    const response = await fetch(`${config.apiUrl}/trip/${route.params.tripId}`)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error || `Erreur ${response.status}: Impossible de charger les données du trajet`
      )
    }

    tripData.value = await response.json()

    if (isAuthenticatedRegularUser.value && userStore.user?.phone) {
      phone.value = userStore.user.phone
    }

    if (userStore.user) {
      await checkIfUserHasRated()
    }
  } catch (err) {
    loadingError.value = err.message
    toast.error(err.message)
  } finally {
    await minTime
    loadingInitial.value = false
  }
})

function addNewPassenger() {
  if (passengers.value.length >= 5) {
    toast.error('Nombre maximum de passagers atteint pour ce trajet.')
    return
  }

  passengers.value.push({
    class: 'Classic',
    type: 'Adult',
    bags: 0
  })
}

function removePassenger(index) {
  if (passengers.value.length > 1) {
    passengers.value.splice(index, 1)
  }
}

const handleRegistrationSuccess = async (registrationData) => {
  pendingRegistrationData.value = {
    ...registrationData,
    phone: phone.value?.trim() || registrationData.phone || '',
    countryIso: registrationData.countryIso || localeStore.countryIso
  }

  registrationSuccess.value = true
  phone.value = pendingRegistrationData.value.phone

  toast.success('Votre compte sera créé avant la confirmation de la réservation.')

  if (registrationFormRef.value) {
    registrationFormRef.value.toggleForm()
  }
}

const handleRegistrationError = (errorMessage) => {
  toast.error(errorMessage)
}

const completeRegistration = async () => {
  if (!pendingRegistrationData.value) return

  const registrationPayload = {
    ...pendingRegistrationData.value,
    phone: phone.value?.trim() || pendingRegistrationData.value.phone || '',
    countryIso: pendingRegistrationData.value.countryIso || localeStore.countryIso
  }

  if (!registrationPayload.phone) {
    throw new Error("Numéro de téléphone manquant pour l'inscription.")
  }

  try {
    const response = await fetch(`${config.apiUrl}/user/register`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(registrationPayload)
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      console.warn('Erreur inscription:', data.errors || data.message)
      throw new Error(data.error || data.message || "Erreur lors de l'inscription.")
    }

    toast.success('Compte créé ! Validez-le plus tard pour accéder à votre espace.')
  } catch (err) {
    console.warn('Erreur lors de la création du compte:', err)
    throw new Error(err.message || "Erreur lors de la création du compte.")
  }
}

async function createReservation() {
  if (!canSkipPayment.value && !hasAvailablePaymentMethods.value) {
    toast.error(
      `Aucun moyen de paiement n'est disponible pour ${currentCountry.value?.name || 'ce pays'}.`
    )
    return
  }

  if (!canSkipPayment.value && paymentMethod.value === '') {
    toast.error('Veuillez choisir un moyen de paiement')
    return
  }

  if (showPhoneField.value && !phone.value.trim()) {
    toast.error('Veuillez saisir un numéro de téléphone')
    return
  }

  if (canSkipPayment.value && !cashCountryIso.value) {
    toast.error('Veuillez sélectionner le pays du client')
    return
  }

  const reservationData = {
    tripUuid: route.params.tripId,
    passengers: passengers.value,
    paymentMethod: paymentMethod.value,
    phone: phone.value.trim(),
    countryIso: canSkipPayment.value
      ? cashCountryIso.value || localeStore.countryIso
      : localeStore.countryIso
  }

  const urlPayment =
    paymentMethod.value === ''
      ? `${config.apiUrl}/ticket/create-without-notchpay`
      : `${config.apiUrl}/ticket/create`

  submitting.value = true

  try {
    if (pendingRegistrationData.value) {
      await completeRegistration()
    }

    const response = await fetch(urlPayment, {
      method: 'POST',
      // credentials: isAnonymous.value ? 'omit' : 'include',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reservationData)
    })

    const result = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(
        result.error || result.message || 'Erreur lors de la création de la réservation.'
      )
    }

    const ticketId = result.ticket || result.ticketUuid || result.uuid || result.id

    if (!ticketId) {
      throw new Error('Identifiant du ticket manquant')
    }

    await sleep(500)
    await router.push({
      name: 'ticket',
      params: { ticketUuid: ticketId }
    })
  } catch (err) {
    toast.error(err.message || 'Erreur lors de la création de la réservation.')
  } finally {
    submitting.value = false
  }
}

function formatDateTime(dateString) {
  if (!dateString) return '—'

  const date = new Date(dateString)

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatPrice(price) {
  return localeStore.formatPrice(price)
}
</script>
<style scoped>
.page-header.center {
  padding-bottom: 2rem;
}

.trip-page {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

/* Route Cards (Départ / Arrivée) */
.route-cards {
  display: flex;
  align-items: center;
  gap: 0rem;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-direction: row;
  position: relative;
}

.route-card {
  flex: 1;
  min-width: 280px;
  padding: 0;
  overflow: hidden;
}

.route-cards .route-card--start {
  border-radius: var(--border-radius-md) 0 0 var(--border-radius-md) !important;
  border-right: 1px solid var(--color-border) !important;
  /* box-shadow: -2px 0 4px rgba(0,0,0,0.1) !important; */
  box-shadow: none;
}

.route-cards .route-card--end {
  border-left: 1px solid var(--color-border) !important;
  border-radius: 0 var(--border-radius-md) var(--border-radius-md) 0 !important;
  /* box-shadow: 2px 0 4px rgba(0,0,0,0.1) !important; */
  box-shadow: none;
}

.payment-help {
  display: block;
  margin-top: 0.45rem;
  color: var(--color-text-secondary);
}

.card-thumbnail {
  width: 100%;
  height: 140px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-jungle), var(--color-feuille));
  position: relative;
}

.card-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-thumbnail.placeholder {
  font-size: 4rem;
  color: white;
}

.label-start,
.label-end {
  position: absolute;
  font-size: 28pt;
  font-weight: 800;
  font-style: italic;
  color: white;
  opacity: 0.7;
  text-transform: uppercase;
  text-shadow: var(--shadow-sm);
}

.route-card .card-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.route-card .card-body {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.city-name {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  text-transform: uppercase;
}

.address {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
}

.time {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-top: 0.5rem;
}

.trip-route-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

/* Connecteur avec ligne pointillée HORIZONTALE */
.trip-connector-container {
  position: absolute;
  left: 50%;
  top: 22%;
  transform: translate(-50%, -50%);
  z-index: 10;
  /* par-dessus les cards */
  pointer-events: none;
  /* ne bloque pas les clics sur les cards */
}

.trip-connector {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

.dotted-line {
  flex: 1;
  min-width: 43px;
  height: 0;
  border-top: 2px dashed var(--color-text-primary);
  opacity: 0.6;
}

.duration-badge {
  color: var(--color-text-primary);
  padding: 0.6rem 1.2rem;
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--color-border);
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
  background: var(--bg-primary);
}

/* Trip Details */
.trip-route-header h2,
.trip-details h2,
.passengers-section h2,
.payment-section h2 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--color-text-primary);
}

.card {
  border: 2px solid var(--color-border);
  overflow: hidden;
  padding: 0;
}

/* CARD ITEM CHAUFFEUR SIMPLIFIÉ */
.driver-info-simple {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.driver-info-simple span {
  font-size: 1rem;
  color: var(--color-text-primary);
  font-weight: 500;
}

.driver-avatar-simple {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--color-jungle);
  flex-shrink: 0;
}

.driver-avatar-simple img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.driver-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-jungle), var(--color-feuille));
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
}

.immat {
  text-transform: uppercase;
  font-weight: 600;
  font-family: monospace;
  background: var(--color-grey-light);
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
}

.price {
  font-size: 1.1rem;
  font-weight: bold;
  color: var(--color-primary);
}

.price-item {
  border-top: 1px solid var(--color-border);
  background: var(--color-background-secondary) !important;
  padding: 1.25rem 1.5rem;
}

.price-total {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-jungle);
}

/* Passengers Section */
.passengers-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.passengers-header h2 {
  margin-bottom: 0;
}

.passengers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.passenger-item {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  background: var(--color-grey-light);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
}

.passenger-inputs {
  display: flex;
  gap: 0.75rem;
  flex: 1;
  flex-wrap: wrap;
}

.passengers-section button {
  display: none;
}

.input-field {
  padding: 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  flex: 1;
  min-width: 120px;
}

.input-field.small {
  min-width: 80px;
  max-width: 100px;
}

.bags-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bags-control label {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
}

.btn-remove {
  appearance: none;
  border: none;
  background: var(--color-danger, #dc3545);
  color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  flex-shrink: 0;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #c82333;
  transform: scale(1.1);
}

/* Payment Section */
.payment-row {
  display: flex;
  gap: 0;
  width: 100%;
}

.payment-row .card-item {
  flex: 1;
  border-bottom: none;
}

.payment-row .card-item--paiment {
  border-right: 1px solid var(--color-border);
  background-color: var(--color-background-secondary) !important;
}

.payment-row .card-item--phone {
  background-color: var(--color-grey-light) !important;
}

.card-item.card-item--paiment {
  flex-flow: column wrap;
}

.card-item.card-item--phone {
  flex-flow: wrap wrap;
  gap: 0.75rem;
}

.card-item.card-item--phone>div:last-child {
  flex: 1;
}

.card-item.card-item--paiment,
.card-item.card-item--phone {
  justify-content: flex-start;
  align-items: flex-start;
}

.card-item.card-item--paiment select,
.card-item.card-item--phone select,
.card-item.card-item--phone input {
  margin-top: 0.5rem;
  width: 100%;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.9rem 2rem;
  font-size: 1.05rem;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-weight: 600;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.registration-prompt-section {
  width: 100%;
}

.company-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-header {}

.trip-page {
  gap: 0;
}

/* petit bricolage a valider si je met du gap ou non... */
.route-cards,
.trip-details,
.passengers-section,
.payment-section,
.inline-registration {
  padding-bottom: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .trip-page {
    gap: 2rem;
  }

  /* petit bricolage a valider... */
  .route-cards,
  .trip-details,
  .passengers-section,
  .payment-section,
  .inline-registration {
    padding-bottom: 0;
  }

  .passengers-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .passengers-header .btn {
    width: 100%;
  }

  .payment-row {
    flex-direction: column;
  }

  .payment-row .card-item--paiment {
    border-right: none;
  }

  .route-cards .trip-route-header {
    display: none;
  }

  .route-cards {
    flex-direction: column;
    gap: 0;
    position: relative;
  }

  .route-card {
    max-width: 100%;
  }

  .route-cards .route-card--start {
    border-radius: var(--border-radius-md) var(--border-radius-md) 0 0 !important;
    border-bottom: 1px solid var(--color-border) !important;
    border-right: 2px solid var(--color-border) !important;
  }

  .route-cards .route-card--end {
    border-top: 1px solid var(--color-border) !important;
    border-left: 2px solid var(--color-border) !important;
    border-radius: 0 0 var(--border-radius-md) var(--border-radius-md) !important;
  }

  /* .route-card:first-child { */
  /*   border-radius: var(--border-radius-md) var(--border-radius-md) 0 0; */
  /* } */
  /**/
  /* .route-card:last-child { */
  /*   border-radius: 0 0 var(--border-radius-md) var(--border-radius-md); */
  /* } */

  /* Connecteur horizontal sur mobile */
  .trip-connector {
    flex-direction: column;
    width: 100%;
    gap: 0rem;
  }

  .dotted-line {
    min-width: auto;
    min-height: 20px;
    border-top: none;
    border-left: 2px dashed var(--color-text-primary);
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .passenger-inputs {
    flex-direction: column;
  }

  .input-field {
    width: 100%;
  }

  .trip-connector-container {
    position: absolute;
    top: 50%;
  }

}

@media (min-width: 768px) {
  .info-box {
    margin-bottom: 3rem;
  }
}
</style>
